import { ETypographyIntents } from '@src/typings';
import { LoadingOutlined } from '@ant-design/icons';
import React, { useMemo, useState } from 'react';
import { HiChevronDown, HiOutlineInformationCircle } from 'react-icons/hi2';
import { Button } from '../Button';
import { withTheme } from '../ThemeProvider';
import { Typography } from '../Typography';
import {
    StyledButtonContent,
    StyledContentLeft,
    StyledDropButtonContainer,
    StyledDropdown,
    StyledMenuItem,
} from './styles';
import { DropButtonProps } from './types';

const DropButtonComponent: React.FC<DropButtonProps> = ({
    items,
    loading,
    disabled,
    icon,
    placement = 'bottomLeft',
    trigger = ['click'],
    arrow = true,
    error,
    errorMessage,
    errorSize = ETypographyIntents.Badge,
    multiSelect = false,
    selectedKeys = [],
    onSelect,
    children,
    className,
    showInfoIcon = true,
    ...props
}: Readonly<DropButtonProps>) => {
    const [open, setOpen] = useState(false);

    const handleMenuClick = (e: { key: string }) => {
        if (multiSelect) {
            const newSelectedKeys = selectedKeys.includes(e.key)
                ? selectedKeys.filter((key) => key !== e.key)
                : [...selectedKeys, e.key];
            onSelect?.(newSelectedKeys);
            setOpen(true);
        } else {
            const newSelectedKeys = selectedKeys.includes(e.key) ? [] : [e.key];
            onSelect?.(newSelectedKeys);
            setOpen(false);
        }
    };

    const menuItems = items?.map((item) => ({
        key: item.key,
        label: (
            <StyledMenuItem>
                <div className="menu-item-content">
                    {item.icon && <item.icon className="h-4 w-4" />}
                    <span aria-disabled={item.disabled}>{item.label}</span>
                </div>
                {item.description && (
                    <span className="menu-item-description">
                        {item.description}
                    </span>
                )}
            </StyledMenuItem>
        ),
        disabled: item.disabled,
    }));

    const menuProps = useMemo(() => {
        return {
            items: menuItems,
            onClick: handleMenuClick,
            selectedKeys: multiSelect ? selectedKeys : selectedKeys.slice(0, 1),
            multiple: multiSelect,
        };
    }, [menuItems, items, selectedKeys, multiSelect]);

    const SelectedItem = () => {
        if (!selectedKeys?.length) return children;
        return <span>{`${selectedKeys.length} item selected`}</span>;
    };

    return (
        <StyledDropButtonContainer className={className}>
            <StyledDropdown
                menu={menuProps}
                placement={placement}
                trigger={trigger}
                arrow={arrow}
                disabled={disabled || loading}
                open={open}
                onOpenChange={(val) => !multiSelect && setOpen(val)}
            >
                <Button
                    {...props}
                    onClick={() => multiSelect && setOpen(!open)}
                    disabled={disabled || loading}
                >
                    <StyledButtonContent>
                        <StyledContentLeft>
                            {!icon && showInfoIcon && (
                                <HiOutlineInformationCircle className="h-4 w-4" />
                            )}
                            {icon && <span>{icon}</span>}
                            <SelectedItem />
                        </StyledContentLeft>
                        {loading ? (
                            <LoadingOutlined className="mx-2" />
                        ) : (
                            <>
                                {arrow && (
                                    <HiChevronDown
                                        className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
                                    />
                                )}
                            </>
                        )}
                    </StyledButtonContent>
                </Button>
            </StyledDropdown>
            {error && errorMessage && (
                <Typography
                    intent={errorSize || ETypographyIntents.Badge}
                    className="text-inputError"
                >
                    {errorMessage}
                </Typography>
            )}
        </StyledDropButtonContainer>
    );
};

export const DropButton = withTheme(DropButtonComponent);
